import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorAlert from '../components/Common/ErrorAlert';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { useDirection } from '../context/DirectionContext';
import { translate as tr, translateOr } from '../i18n/translations';
import apiService from '../services/api';
import {
  AssignInterviewerToCandidatesDto,
  BulkTeamAssignmentDto,
  CreateCycleDto,
  Cycle,
  CycleStatus,
  FormTemplate,
  Station,
  User,
  UserRole,
} from '../types/api';
import { getErrorMessage } from '../utils/errorUtils';

// ── Types for wizard rows ─────────────────────────────────────────────────────

type CandRow   = { _id: string; num: string; team: string };
type TeamRow   = { _id: string; userId: string; teamNum: string; role: UserRole | '' };
type IntRow    = { _id: string; userId: string; candidateIds: string[] };

const uid = () => Math.random().toString(36).slice(2);

// ── Validation helpers ────────────────────────────────────────────────────────

const isValidFormat     = (v: string) => /^(0[1-9]|1[0-2])-\d{4}$/.test(v);
const isValidDate       = (v: string) => !v || !isNaN(new Date(v).getTime());
const isFutureDate      = (v: string) => { if (!v) return true; const d = new Date(v); if (isNaN(d.getTime())) return false; const t = new Date(); t.setHours(0,0,0,0); return d >= t; };
const formatMonthYear   = (v: string) => { const d = v.replace(/\D/g,'').slice(0,6); return d.length >= 3 ? `${d.slice(0,2)}-${d.slice(2)}` : d; };
const getCurrentMY      = () => { const n = new Date(); return `${String(n.getMonth()+1).padStart(2,'0')}-${n.getFullYear()}`; };
const isInt             = (v: string) => /^\d+$/.test(v.trim()) && parseInt(v.trim()) > 0;

const TEAM_ROLES = [UserRole.TEAMLEAD, UserRole.ASSESSOR, UserRole.SOCIO, UserRole.INTERVIEWER, UserRole.VIEWER];

// ── CreateCycleWizard ─────────────────────────────────────────────────────────

interface WizardProps {
  open: boolean;
  onClose: () => void;
  existingCycles: Cycle[];
}

const CreateCycleWizard: React.FC<WizardProps> = ({ open, onClose, existingCycles }) => {
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();
  const { lang }      = useDirection();

  const WIZARD_STEPS = [
    tr('wizard.step.basics', lang),
    tr('wizard.step.schedule', lang),
    tr('wizard.step.review', lang),
    tr('wizard.step.forms', lang),
    tr('wizard.step.stations', lang),
    tr('wizard.step.candidates', lang),
    tr('wizard.step.team', lang),
    tr('wizard.step.interviews', lang),
  ];

  // Step state
  const [step, setStep]   = useState(0);
  const [touched, setTouched] = useState<Record<string,boolean>>({});
  const [stepError, setStepError] = useState('');

  // Pre-creation data
  const [displayName, setDisplayName] = useState('');
  const [monthYear, setMonthYear]     = useState('');
  const [startedAt, setStartedAt]     = useState('');
  const [status, setStatus]           = useState<CycleStatus>(CycleStatus.DRAFT);

  // Post-creation
  const [createdCycleId, setCreatedCycleId] = useState('');
  const [candRows, setCandRows]   = useState<CandRow[]>([]);
  const [teamRows, setTeamRows]   = useState<TeamRow[]>([]);
  const [intRows, setIntRows]     = useState<IntRow[]>([]);
  // bank selection
  const [selectedFormIds, setSelectedFormIds]       = useState<string[]>([]);
  const [formWeights, setFormWeights]               = useState<Record<string, number>>({});
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>([]);
  // interview builder
  const [intUser, setIntUser]         = useState<User | null>(null);
  const [intCandIds, setIntCandIds]   = useState<string[]>([]);

  // Queries (enabled only when needed)
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn:  () => apiService.getUsers(),
    enabled:  step >= 6 && open,
  });
  const { data: bankForms = [] } = useQuery({
    queryKey: ['formTemplates'],
    queryFn:  () => apiService.getFormTemplates(),
    enabled:  step === 3 && open,
  });
  const { data: bankStations = [] } = useQuery({
    queryKey: ['bankStations'],
    queryFn:  () => apiService.getBankStations(),
    enabled:  step === 4 && open,
  });
  const { data: cycleSt, isLoading: stLoading } = useQuery({
    queryKey: ['cycleStatus', createdCycleId],
    queryFn:  () => apiService.getCycleStatus(createdCycleId),
    enabled:  step === 7 && !!createdCycleId && open,
  });
  const cycleCandidates = cycleSt?.teams?.flatMap(t => t.candidates) ?? [];

  // Mutations
  const createMut = useMutation({
    mutationFn: (d: CreateCycleDto) => apiService.createCycle(d),
    onSuccess: (cycle) => {
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
      setCreatedCycleId(cycle.id);
      setStepError('');
      setStep(3);
    },
    onError: (e: any) => setStepError(getErrorMessage(e)),
  });
  const candMut = useMutation({
    mutationFn: ({ cycleId, file }: { cycleId: string; file: File }) => apiService.uploadCandidatesCSV(cycleId, file),
    onError: (e: any) => setStepError(getErrorMessage(e)),
  });
  const teamMut = useMutation({
    mutationFn: ({ cycleId, dto }: { cycleId: string; dto: BulkTeamAssignmentDto }) => apiService.assignToTeams(cycleId, dto),
    onError: (e: any) => setStepError(getErrorMessage(e)),
  });
  const intMut = useMutation({
    mutationFn: ({ cycleId, dto }: { cycleId: string; dto: AssignInterviewerToCandidatesDto }) => apiService.assignInterviewerToCandidates(cycleId, dto),
    onError: (e: any) => setStepError(getErrorMessage(e)),
  });

  const createAssignmentsMut = useMutation({
    mutationFn: ({ cycleId, formIds, weights }: { cycleId: string; formIds: string[]; weights: Record<string, number> }) =>
      Promise.all(formIds.map(templateId =>
        apiService.createFormAssignment({ templateId, cycleId, weightInFinalScore: weights[templateId] ?? 1.0 })
      )),
    onError: (e: any) => setStepError(getErrorMessage(e)),
  });
  const cloneStationsMut = useMutation({
    mutationFn: ({ cycleId, stationIds }: { cycleId: string; stationIds: string[] }) =>
      apiService.cloneStationsIntoCycle(cycleId, stationIds),
    onError: (e: any) => setStepError(getErrorMessage(e)),
  });

  const isPending = createMut.isPending || candMut.isPending || teamMut.isPending || intMut.isPending || createAssignmentsMut.isPending || cloneStationsMut.isPending;

  const handleClose = useCallback(() => {
    setStep(0); setDisplayName(''); setMonthYear(''); setStartedAt(''); setStatus(CycleStatus.DRAFT);
    setCreatedCycleId(''); setCandRows([]); setTeamRows([]); setIntRows([]);
    setSelectedFormIds([]); setFormWeights({}); setSelectedStationIds([]);
    setIntUser(null); setIntCandIds([]); setStepError(''); setTouched({});
    onClose();
  }, [onClose]);

  const finish = () => { handleClose(); if (createdCycleId) navigate(`/cycles/${createdCycleId}`); };

  // ── Validation ──────────────────────────────────────────────────────────────

  const step0Valid = !!displayName.trim() && isValidFormat(monthYear);
  const step1Valid = !startedAt || (isValidDate(startedAt) && isFutureDate(startedAt));

  // ── Step 0: Basics ──────────────────────────────────────────────────────────

  const renderBasics = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2.5 }}>{tr('wizard.basics.desc', lang)}</Typography>
      <TextField
        autoFocus label={tr('wizard.displayName', lang)} fullWidth sx={{ mb: 2.5 }}
        value={displayName} placeholder={tr('cyc.egName', lang)}
        onChange={e => setDisplayName(e.target.value)}
        onBlur={() => setTouched(t => ({...t, displayName: true}))}
        error={touched.displayName && !displayName.trim()}
        helperText={touched.displayName && !displayName.trim() ? tr('common.required', lang) : ''}
      />
      <TextField
        label={tr('wizard.monthYear', lang)} fullWidth placeholder={getCurrentMY()}
        value={monthYear} inputProps={{ maxLength: 7, inputMode: 'numeric' }}
        onChange={e => { setMonthYear(formatMonthYear(e.target.value)); setTouched(t => ({...t, monthYear: true})); }}
        error={touched.monthYear && !!(monthYear && !isValidFormat(monthYear))}
        helperText={
          touched.monthYear && monthYear && !isValidFormat(monthYear) ? tr('wizard.myInvalid', lang) :
          tr('wizard.myHint', lang)
        }
      />
    </Box>
  );

  // ── Step 1: Schedule ────────────────────────────────────────────────────────

  const renderSchedule = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2.5 }}>{tr('wizard.schedule.desc', lang)}</Typography>
      <TextField
        label={tr('wizard.startDate', lang)} type="date" fullWidth InputLabelProps={{ shrink: true }}
        value={startedAt ? startedAt.split('T')[0] : ''}
        inputProps={{ min: new Date().toISOString().split('T')[0] }}
        onChange={e => setStartedAt(e.target.value ? `${e.target.value}T00:00:00.000Z` : '')}
        error={!!(startedAt && (!isValidDate(startedAt) || !isFutureDate(startedAt)))}
        helperText={startedAt && !isValidDate(startedAt) ? tr('wizard.invalidDate', lang) : startedAt && !isFutureDate(startedAt) ? tr('wizard.futureDate', lang) : tr('wizard.startDateHint', lang)}
        sx={{ mb: 2.5 }}
      />
      <FormControl fullWidth>
        <InputLabel>{tr('wizard.initialStatus', lang)}</InputLabel>
        <Select value={status} label={tr('wizard.initialStatus', lang)} onChange={e => setStatus(e.target.value as CycleStatus)}>
          <MenuItem value={CycleStatus.DRAFT}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#F59E0B' }} />
              <Box><Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{tr('wizard.draft.title', lang)}</Typography><Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{tr('wizard.draft.desc', lang)}</Typography></Box>
            </Box>
          </MenuItem>
          <MenuItem value={CycleStatus.ACTIVE}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
              <Box><Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{tr('wizard.active.title', lang)}</Typography><Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{tr('wizard.active.desc', lang)}</Typography></Box>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
      {status === CycleStatus.ACTIVE && <Alert severity="warning" sx={{ mt: 2 }}>{tr('wizard.activeWarn', lang)}</Alert>}
    </Box>
  );

  // ── Step 2: Review ──────────────────────────────────────────────────────────

  const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25, borderBottom: '1px solid #F1F5F9' }}>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>{label}</Typography>
      {value}
    </Box>
  );

  const renderReview = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2 }}>{tr('wizard.review.desc', lang)}</Typography>
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, px: 2.5, py: 0.5, mb: 2 }}>
        <InfoRow label={tr('wizard.displayNameLabel', lang)} value={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{displayName}</Typography>} />
        <InfoRow label={tr('wizard.monthYearLabel', lang)} value={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600, fontFamily: 'monospace' }}>{monthYear}</Typography>} />
        <InfoRow label={tr('wizard.startDateLabel', lang)} value={startedAt ? <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{new Date(startedAt).toLocaleDateString()}</Typography> : <Typography sx={{ fontSize: '0.875rem', color: '#94A3B8' }}>{tr('common.notSet', lang)}</Typography>} />
        <InfoRow label={tr('wizard.initialStatusLabel', lang)} value={<Chip label={translateOr(`cycleStatus.${status}`, lang, status)} color={status === CycleStatus.ACTIVE ? 'success' : 'warning'} size="small" />} />
      </Paper>
      {stepError && <Alert severity="error">{stepError}</Alert>}
    </Box>
  );

  // ── Step 3: Forms Bank ──────────────────────────────────────────────────────

  const totalWeight = selectedFormIds.reduce((sum, id) => sum + (formWeights[id] ?? 1.0), 0);
  const weightOk    = totalWeight <= 1.0 + 1e-9;

  const toggleFormSelection = (id: string) => {
    setSelectedFormIds(ids => {
      if (ids.includes(id)) return ids.filter(x => x !== id);
      if (!(id in formWeights)) setFormWeights(w => ({ ...w, [id]: 1.0 }));
      return [...ids, id];
    });
  };

  const renderFormsBank = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2 }}>
        {tr('wizard.forms.desc', lang)}
      </Typography>
      {bankForms.length === 0 ? (
        <Alert severity="info">{tr('wizard.noForms', lang)}</Alert>
      ) : (
        <List dense disablePadding sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
          {(bankForms as FormTemplate[]).map((form, i) => {
            const selected = selectedFormIds.includes(form.id);
            return (
              <ListItem
                key={form.id}
                disablePadding
                sx={{ borderBottom: i < bankForms.length - 1 ? '1px solid #F1F5F9' : 'none', px: 1.5, py: 0.75 }}
              >
                <Checkbox
                  size="small"
                  checked={selected}
                  onChange={() => toggleFormSelection(form.id)}
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemText
                  primary={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0F172A' }}>{form.name}</Typography>}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                      <Chip label={form.formType} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 18 }} />
                    </Box>
                  }
                />
                {selected && (
                  <TextField
                    size="small"
                    label={tr('common.weight', lang)}
                    type="number"
                    value={formWeights[form.id] ?? 1.0}
                    onChange={e => setFormWeights(w => ({ ...w, [form.id]: parseFloat(e.target.value) || 0 }))}
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    sx={{ width: 90, flexShrink: 0 }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      )}
      {selectedFormIds.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#6366F1', fontWeight: 600 }}>
            {selectedFormIds.length} {tr('wizard.formsSelected', lang)}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: weightOk ? '#10B981' : '#EF4444' }}>
            {tr('wizard.totalWeight', lang)} {totalWeight.toFixed(2)} / 1.00
          </Typography>
          {!weightOk && <Typography sx={{ fontSize: '0.72rem', color: '#EF4444' }}>{tr('wizard.exceedsWeight', lang)}</Typography>}
        </Box>
      )}
      {stepError && <Alert severity="error" sx={{ mt: 1.5 }}>{stepError}</Alert>}
    </Box>
  );

  const handleConfirmForms = async () => {
    if (selectedFormIds.length === 0) { setStepError(''); setStep(4); return; }
    if (!weightOk) { setStepError(tr('wizard.exceedsWeightErr', lang)); return; }
    try {
      await createAssignmentsMut.mutateAsync({ cycleId: createdCycleId, formIds: selectedFormIds, weights: formWeights });
      setStepError('');
      setStep(4);
    } catch { /* handled by onError */ }
  };

  // ── Step 4: Stations Bank ────────────────────────────────────────────────────

  const renderStationsBank = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2 }}>
        {tr('wizard.stations.desc', lang)}
      </Typography>
      {bankStations.length === 0 ? (
        <Alert severity="info">{tr('wizard.noStations', lang)}</Alert>
      ) : (
        <List dense disablePadding sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
          {bankStations.map((station: Station, i: number) => (
            <ListItem
              key={station.id}
              disablePadding
              sx={{ borderBottom: i < bankStations.length - 1 ? '1px solid #F1F5F9' : 'none', px: 1.5, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#F8FAFC' } }}
              onClick={() => setSelectedStationIds(ids => ids.includes(station.id) ? ids.filter(id => id !== station.id) : [...ids, station.id])}
            >
              <Checkbox
                size="small"
                checked={selectedStationIds.includes(station.id)}
                onChange={() => {}}
                sx={{ p: 0.5, mr: 1 }}
              />
              <ListItemText
                primary={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0F172A' }}>{station.name}</Typography>}
                secondary={
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                    <Chip label={station.stationType} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 18 }} />
                    <Typography component="span" sx={{ fontSize: '0.72rem', color: '#94A3B8', alignSelf: 'center' }}>{tr('common.level', lang)} {station.stationLevel}</Typography>
                    {station.description && <Typography component="span" sx={{ fontSize: '0.72rem', color: '#94A3B8', alignSelf: 'center' }}>· {station.description}</Typography>}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      {selectedStationIds.length > 0 && (
        <Typography sx={{ mt: 1.5, fontSize: '0.78rem', color: '#6366F1', fontWeight: 600 }}>
          {selectedStationIds.length} {tr('wizard.stationsSelected', lang)}
        </Typography>
      )}
      {stepError && <Alert severity="error" sx={{ mt: 1.5 }}>{stepError}</Alert>}
    </Box>
  );

  const handleConfirmStations = async () => {
    if (selectedStationIds.length === 0) { setStepError(''); setStep(5); return; }
    try {
      await cloneStationsMut.mutateAsync({ cycleId: createdCycleId, stationIds: selectedStationIds });
      setStepError('');
      setStep(5);
    } catch { /* handled by onError */ }
  };

  // ── Step 5: Candidates ──────────────────────────────────────────────────────

  const candErrors = (): string => {
    if (candRows.length === 0) return '';
    if (candRows.some(r => !isInt(r.num) || !isInt(r.team))) return tr('wizard.allRowsValid', lang);
    const nums = candRows.map(r => r.num.trim());
    if (new Set(nums).size !== nums.length) return tr('wizard.dupCands', lang);
    return '';
  };

  const renderCandidates = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2 }}>
        {tr('wizard.candidates.desc', lang)}
      </Typography>
      {candRows.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            <Typography sx={{ flex: 1, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('wizard.candNum', lang)}</Typography>
            <Typography sx={{ flex: 1, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('wizard.teamNum', lang)}</Typography>
            <Box sx={{ width: 36 }} />
          </Box>
          {candRows.map((row, i) => (
            <Box key={row._id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField size="small" type="number" placeholder={tr('cyc.egNumber', lang)} sx={{ flex: 1 }}
                value={row.num} onChange={e => setCandRows(rs => rs.map((r,j) => j===i ? {...r, num: e.target.value} : r))}
                error={!!row.num && !isInt(row.num)} />
              <TextField size="small" type="number" placeholder={tr('cyc.egTeam', lang)} sx={{ flex: 1 }}
                value={row.team} onChange={e => setCandRows(rs => rs.map((r,j) => j===i ? {...r, team: e.target.value} : r))}
                error={!!row.team && !isInt(row.team)} />
              <IconButton size="small" onClick={() => setCandRows(rs => rs.filter((_,j) => j!==i))} sx={{ color: '#EF4444' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      <Button size="small" startIcon={<AddIcon />} onClick={() => setCandRows(rs => [...rs, { _id: uid(), num: '', team: '' }])} sx={{ mt: 0.5 }}>
        {tr('common.addRow', lang)}
      </Button>
      {candRows.length > 0 && <Typography sx={{ mt: 1, fontSize: '0.78rem', color: '#64748B' }}>{candRows.length} {tr('wizard.candsReady', lang)}</Typography>}
      {(stepError || candErrors()) && <Alert severity="error" sx={{ mt: 1.5 }}>{stepError || candErrors()}</Alert>}
    </Box>
  );

  const handleConfirmCandidates = async () => {
    const err = candErrors();
    if (err) { setStepError(err); return; }
    if (candRows.length === 0) { setStep(6); return; }
    const csv = 'candidate_number,team_number\n' + candRows.map(r => `${r.num.trim()},${r.team.trim()}`).join('\n');
    const file = new File([csv], 'candidates.csv', { type: 'text/csv' });
    try {
      await candMut.mutateAsync({ cycleId: createdCycleId, file });
      queryClient.invalidateQueries({ queryKey: ['cycleStatus', createdCycleId] });
      setStepError('');
      setStep(6);
    } catch { /* handled by onError */ }
  };

  // ── Step 6: Team Assignments ─────────────────────────────────────────────────

  const teamErrors = (): string => {
    if (teamRows.length === 0) return '';
    if (teamRows.some(r => !r.userId || !isInt(r.teamNum) || !r.role)) return tr('wizard.allTeamRowsValid', lang);
    const ids = teamRows.map(r => r.userId);
    if (new Set(ids).size !== ids.length) return tr('wizard.oneAssignment', lang);
    return '';
  };

  const renderTeam = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2 }}>
        {tr('wizard.team.desc', lang)}
      </Typography>
      {teamRows.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            <Typography sx={{ flex: 2, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('wizard.user', lang)}</Typography>
            <Typography sx={{ width: 80, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('wizard.teamNum', lang)}</Typography>
            <Typography sx={{ flex: 1, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('common.role', lang)}</Typography>
            <Box sx={{ width: 36 }} />
          </Box>
          {teamRows.map((row, i) => (
            <Box key={row._id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <Autocomplete
                sx={{ flex: 2 }} size="small"
                options={users} getOptionLabel={u => `${u.firstName} ${u.lastName} (${u.email})`}
                value={users.find(u => u.id === row.userId) || null}
                onChange={(_, v) => setTeamRows(rs => rs.map((r,j) => j===i ? {...r, userId: v?.id ?? ''} : r))}
                renderInput={p => <TextField {...p} placeholder={tr('wizard.selectUser', lang)} />}
              />
              <TextField size="small" type="number" placeholder="1" sx={{ width: 80 }}
                value={row.teamNum} onChange={e => setTeamRows(rs => rs.map((r,j) => j===i ? {...r, teamNum: e.target.value} : r))}
                error={!!row.teamNum && !isInt(row.teamNum)} />
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select value={row.role} displayEmpty onChange={e => setTeamRows(rs => rs.map((r,j) => j===i ? {...r, role: e.target.value as UserRole} : r))}>
                  <MenuItem value="" disabled><em>{tr('common.role', lang)}</em></MenuItem>
                  {TEAM_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
              <IconButton size="small" onClick={() => setTeamRows(rs => rs.filter((_,j) => j!==i))} sx={{ color: '#EF4444' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      <Button size="small" startIcon={<PersonAddIcon />} onClick={() => setTeamRows(rs => [...rs, { _id: uid(), userId: '', teamNum: '', role: '' as any }])} sx={{ mt: 0.5 }}>
        {tr('common.addRow', lang)}
      </Button>
      {(stepError || teamErrors()) && <Alert severity="error" sx={{ mt: 1.5 }}>{stepError || teamErrors()}</Alert>}
    </Box>
  );

  const handleConfirmTeam = async () => {
    const err = teamErrors();
    if (err) { setStepError(err); return; }
    if (teamRows.length === 0) { setStep(7); return; }
    try {
      await teamMut.mutateAsync({
        cycleId: createdCycleId,
        dto: { assignments: teamRows.map(r => ({ userId: r.userId, teamNumber: parseInt(r.teamNum), role: r.role as UserRole })) },
      });
      setStepError('');
      setStep(7);
    } catch { /* handled */ }
  };

  // ── Step 7: Interview Assignments ────────────────────────────────────────────

  const renderInterviews = () => (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2 }}>
        {tr('wizard.interviews.desc', lang)}
      </Typography>
      {stLoading ? (
        <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8' }}>{tr('wizard.loadingCands', lang)}</Typography>
      ) : cycleCandidates.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>{tr('wizard.noCands', lang)}</Alert>
      ) : (
        <>
          {/* Builder row */}
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A', mb: 1.5 }}>{tr('wizard.addInterview', lang)}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: 180 }} size="small"
                options={users} getOptionLabel={u => `${u.firstName} ${u.lastName}`}
                value={intUser} onChange={(_, v) => setIntUser(v)}
                renderInput={p => <TextField {...p} label={tr('wizard.interviewer', lang)} />}
              />
              <Autocomplete
                sx={{ flex: 2, minWidth: 220 }} size="small" multiple
                options={cycleCandidates}
                getOptionLabel={c => `#${c.candidateNumber} (Team ${c.teamNumber})`}
                value={cycleCandidates.filter(c => intCandIds.includes(c.id))}
                onChange={(_, v) => setIntCandIds(v.map(c => c.id))}
                renderInput={p => <TextField {...p} label={tr('wizard.candidates.label', lang)} />}
                renderTags={(v, getProps) => v.map((c, i) => { const { key, ...tagProps } = getProps({ index: i }) as any; return <Chip key={c.id} label={`#${c.candidateNumber}`} size="small" {...tagProps} />; })}
              />
              <Button
                variant="outlined" size="small"
                disabled={!intUser || intCandIds.length === 0}
                onClick={() => {
                  if (!intUser || intCandIds.length === 0) return;
                  setIntRows(rs => {
                    const existing = rs.find(r => r.userId === intUser.id);
                    if (existing) {
                      return rs.map(r => r.userId === intUser.id ? { ...r, candidateIds: Array.from(new Set([...r.candidateIds, ...intCandIds])) } : r);
                    }
                    return [...rs, { _id: uid(), userId: intUser.id, candidateIds: intCandIds }];
                  });
                  setIntCandIds([]);
                }}
              >
                {tr('common.add', lang)}
              </Button>
            </Box>
          </Paper>

          {/* Accumulated rows */}
          {intRows.length > 0 && (
            <Box>
              {intRows.map((row, i) => {
                const u = users.find(u => u.id === row.userId);
                return (
                  <Box key={row._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0F172A', minWidth: 140 }}>
                      {u ? `${u.firstName} ${u.lastName}` : row.userId}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                      {row.candidateIds.map(cid => {
                        const c = cycleCandidates.find(c => c.id === cid);
                        return <Chip key={cid} label={c ? `#${c.candidateNumber}` : cid} size="small" variant="outlined" color="primary" />;
                      })}
                    </Box>
                    <IconButton size="small" onClick={() => setIntRows(rs => rs.filter((_,j) => j!==i))} sx={{ color: '#EF4444' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}
      {stepError && <Alert severity="error" sx={{ mt: 1.5 }}>{stepError}</Alert>}
    </Box>
  );

  const handleConfirmInterviews = async () => {
    if (intRows.length === 0) { finish(); return; }
    try {
      for (const row of intRows) {
        await intMut.mutateAsync({ cycleId: createdCycleId, dto: { userId: row.userId, candidateIds: row.candidateIds } });
      }
      setStepError('');
      finish();
    } catch { /* handled */ }
  };

  // ── Wizard frame ─────────────────────────────────────────────────────────────

  const isPostCreation = step >= 3;
  const isLastStep = step === WIZARD_STEPS.length - 1;

  const handleNext = () => {
    if (step === 0) { setTouched({ displayName: true, monthYear: true }); if (!step0Valid) return; setStepError(''); setStep(1); return; }
    if (step === 1) { if (!step1Valid) return; setStepError(''); setStep(2); return; }
    if (step === 2) {
      createMut.mutate({ displayName, monthYear, status, startedAt: startedAt || undefined });
      return;
    }
    if (step === 3) { handleConfirmForms(); return; }
    if (step === 4) { handleConfirmStations(); return; }
    if (step === 5) { handleConfirmCandidates(); return; }
    if (step === 6) { handleConfirmTeam(); return; }
    if (step === 7) { handleConfirmInterviews(); return; }
  };

  const handleSkip = () => {
    if (isLastStep) { finish(); return; }
    setStepError('');
    setStep(s => s + 1);
  };

  const nextLabel = () => {
    if (step === 2) return createMut.isPending ? tr('wizard.creating', lang) : tr('wizard.createCycle', lang);
    if (isLastStep) return isPending ? tr('common.saving', lang) : tr('common.finish', lang);
    if (step === 3) return createAssignmentsMut.isPending ? tr('common.adding', lang) : (selectedFormIds.length > 0 ? `${tr('wizard.addForms', lang)} (${selectedFormIds.length})` : tr('common.next', lang));
    if (step === 4) return cloneStationsMut.isPending ? tr('common.adding', lang) : (selectedStationIds.length > 0 ? `${tr('wizard.addStations', lang)} (${selectedStationIds.length})` : tr('common.next', lang));
    if (step === 5) return candMut.isPending ? tr('common.uploading', lang) : (candRows.length > 0 ? tr('wizard.uploadCandidates', lang) : tr('common.next', lang));
    if (step === 6) return teamMut.isPending ? tr('common.assigning', lang) : (teamRows.length > 0 ? tr('wizard.assignTeam', lang) : tr('common.next', lang));
    return tr('common.next', lang);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{tr('wizard.title', lang)}</Typography>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 2 }}>
        <Stepper activeStep={step} alternativeLabel>
          {WIZARD_STEPS.map((label, i) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
                {label}
                {i >= 3 && <Typography component="span" sx={{ fontSize: '0.6rem', color: '#94A3B8', ml: 0.25 }}>{tr('wizard.opt', lang)}</Typography>}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {step === 0 && renderBasics()}
        {step === 1 && renderSchedule()}
        {step === 2 && renderReview()}
        {step === 3 && renderFormsBank()}
        {step === 4 && renderStationsBank()}
        {step === 5 && renderCandidates()}
        {step === 6 && renderTeam()}
        {step === 7 && renderInterviews()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {isPostCreation ? (
          <Button onClick={handleClose} color="inherit" sx={{ mr: 'auto' }}>{tr('common.close', lang)}</Button>
        ) : (
          <Button onClick={handleClose} color="inherit" sx={{ mr: 'auto' }}>{tr('common.cancel', lang)}</Button>
        )}
        {!isPostCreation && step > 0 && (
          <Button onClick={() => { setStepError(''); setStep(s => s - 1); }} startIcon={<ArrowBackIcon />} color="inherit">{tr('common.back', lang)}</Button>
        )}
        {isPostCreation && (
          <Button onClick={handleSkip} color="inherit" endIcon={<ArrowForwardIcon />}>{tr('common.skip', lang)}</Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={isPending || (step === 2 && !step0Valid)}
          endIcon={step < 2 ? <ArrowForwardIcon /> : step === 2 ? <CheckIcon /> : undefined}
        >
          {nextLabel()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── EditDialog ────────────────────────────────────────────────────────────────

interface EditProps {
  open: boolean; cycle: Cycle; onClose: () => void;
  onSubmit: (d: Partial<CreateCycleDto>) => void;
  isLoading: boolean; existingCycles: Cycle[]; serverError: string;
}
const EditDialog: React.FC<EditProps> = ({ open, cycle, onClose, onSubmit, isLoading, existingCycles, serverError }) => {
  const { lang } = useDirection();
  const [data, setData] = useState<CreateCycleDto>({ displayName: cycle.displayName, monthYear: cycle.monthYear, status: cycle.status, startedAt: cycle.startedAt });
  const [error, setError] = useState('');
  React.useEffect(() => { setData({ displayName: cycle.displayName, monthYear: cycle.monthYear, status: cycle.status, startedAt: cycle.startedAt }); setError(''); }, [cycle]);

  const isDuplicate = (v: string) => existingCycles.some(c => c.monthYear === v && c.id !== cycle.id);
  const isValid = () => !!data.displayName?.trim() && isValidFormat(data.monthYear) && !isDuplicate(data.monthYear) && (!data.startedAt || isValidDate(data.startedAt));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>{tr('wizard.editCycle', lang)}</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {(error || serverError) && <Alert severity="error" sx={{ mb: 2 }}>{error || serverError}</Alert>}
        <TextField autoFocus label={tr('wizard.displayName', lang)} fullWidth value={data.displayName} onChange={e => setData(d => ({...d, displayName: e.target.value}))} sx={{ mb: 2 }} />
        <TextField label={tr('wizard.monthYear', lang)} fullWidth value={data.monthYear}
          onChange={e => setData(d => ({...d, monthYear: formatMonthYear(e.target.value)}))}
          inputProps={{ maxLength: 7 }}
          error={!!(data.monthYear && (!isValidFormat(data.monthYear) || isDuplicate(data.monthYear)))}
          helperText={data.monthYear && !isValidFormat(data.monthYear) ? tr('wizard.formatHint', lang) : data.monthYear && isDuplicate(data.monthYear) ? tr('wizard.alreadyExists', lang) : ''}
          sx={{ mb: 2 }} />
        <TextField label={tr('wizard.startDateLabel', lang)} type="date" fullWidth InputLabelProps={{ shrink: true }}
          value={data.startedAt ? data.startedAt.split('T')[0] : ''}
          onChange={e => setData(d => ({...d, startedAt: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined}))} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">{tr('common.cancel', lang)}</Button>
        <Button variant="contained" onClick={() => onSubmit(data)} disabled={isLoading || !isValid()}>{tr('common.save', lang)}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Cycle status helpers ──────────────────────────────────────────────────────

const statusColors = (status: CycleStatus) => {
  if (status === CycleStatus.ACTIVE) return { border: '#10B981', chip: 'success' as const };
  if (status === CycleStatus.DRAFT)  return { border: '#F59E0B', chip: 'warning' as const };
  return { border: '#CBD5E1', chip: 'default' as const };
};

// ── Main Cycles page ──────────────────────────────────────────────────────────

const Cycles: React.FC = () => {
  const queryClient  = useQueryClient();
  const navigate     = useNavigate();
  const { lang }     = useDirection();

  const [filter, setFilter]         = useState<'all' | CycleStatus>('all');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<Cycle | null>(null);
  const [mutationError, setMutationError] = useState('');

  const { data: cycles, isLoading, error: queryError } = useQuery({ queryKey: ['cycles'], queryFn: () => apiService.getCycles() });

  const updateMut = useMutation({
    mutationFn: ({ id, ...d }: { id: string } & Partial<CreateCycleDto>) => apiService.updateCycle(id, d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['cycles'] }); setEditingCycle(null); setMutationError(''); },
    onError: (e: any) => setMutationError(getErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiService.deleteCycle(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cycles'] }),
    onError: (e: any) => setMutationError(getErrorMessage(e)),
  });
  const closeMut = useMutation({
    mutationFn: (id: string) => apiService.closeCycle(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cycles'] }),
    onError: (e: any) => setMutationError(getErrorMessage(e)),
  });

  const handleActivate = (c: Cycle) => {
    const active = cycles?.find(x => x.status === CycleStatus.ACTIVE);
    if (active) { setMutationError(tr('cycles.cannotActivate', lang)); return; }
    if (window.confirm(tr('cycles.confirmActivate', lang))) updateMut.mutate({ id: c.id, status: CycleStatus.ACTIVE });
  };

  const filtered = (cycles ?? []).filter(c => filter === 'all' || c.status === filter);
  const countBy  = (s: CycleStatus) => cycles?.filter(c => c.status === s).length ?? 0;
  const hasActive = cycles?.some(c => c.status === CycleStatus.ACTIVE) ?? false;

  if (isLoading) return <LoadingSpinner message={tr('cycles.loading', lang)} />;
  if (queryError) return <ErrorAlert error={queryError} title={tr('cycles.errorLoad', lang)} />;

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={{ color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>{tr('common.mgmt', lang)}</Typography>
          <Typography variant="h5">{tr('nav.cycles', lang)}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setMutationError(''); setWizardOpen(true); }}>{tr('cycles.new', lang)}</Button>
      </Box>

      {mutationError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError('')}>{mutationError}</Alert>}

      <Tabs value={filter} onChange={(_, v) => setFilter(v)} sx={{ mb: 3 }}>
        <Tab label={`${tr('cycles.tab.all', lang)} (${cycles?.length ?? 0})`} value="all" />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>{tr('cycles.tab.active', lang)}{countBy(CycleStatus.ACTIVE) > 0 && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981' }} />}</Box>} value={CycleStatus.ACTIVE} />
        <Tab label={`${tr('cycles.tab.draft', lang)} (${countBy(CycleStatus.DRAFT)})`} value={CycleStatus.DRAFT} />
        <Tab label={`${tr('cycles.tab.closed', lang)} (${countBy(CycleStatus.CLOSED)})`} value={CycleStatus.CLOSED} />
      </Tabs>

      {filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, border: '2px dashed #E2E8F0', borderRadius: 3, color: '#94A3B8' }}>
          <Typography sx={{ mb: 1.5 }}>{tr('cycles.noCycles', lang)}</Typography>
          {filter === 'all' && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>{tr('cycles.createFirst', lang)}</Button>}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 2 }}>
          {filtered.map((cycle, i) => {
            const sc = statusColors(cycle.status);
            return (
              <motion.div key={cycle.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: i * 0.04 }}>
                <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #E2E8F0', borderLeft: `4px solid ${sc.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.06)', '&:hover': { boxShadow: '0 4px 14px 0 rgb(0 0 0 / 0.09)' }, transition: 'box-shadow 0.15s' }}>
                  <Box sx={{ px: 2.5, pt: 2.5, pb: 2, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
                      <Chip label={translateOr(`cycleStatus.${cycle.status}`, lang, cycle.status)} color={sc.chip} size="small" />
                      <Typography sx={{ color: '#94A3B8', fontSize: '0.78rem', fontWeight: 600 }}>{cycle.monthYear}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0F172A', lineHeight: 1.35, mb: 1.25 }}>{cycle.displayName}</Typography>
                    {cycle.startedAt && <Typography sx={{ fontSize: '0.775rem', color: '#64748B', mb: 0.2 }}>{tr('cycles.started', lang)} · {new Date(cycle.startedAt).toLocaleDateString()}</Typography>}
                    {cycle.endedAt && <Typography sx={{ fontSize: '0.775rem', color: '#64748B', mb: 0.2 }}>{tr('cycles.ended', lang)} · {new Date(cycle.endedAt).toLocaleDateString()}</Typography>}
                    <Typography sx={{ fontSize: '0.775rem', color: '#94A3B8' }}>{tr('common.created', lang)} · {new Date(cycle.createdAt).toLocaleDateString()}</Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid #F1F5F9', display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />} onClick={() => navigate(`/cycles/${cycle.id}`)} sx={{ color: '#6366F1', fontSize: '0.8rem' }}>{tr('common.view', lang)}</Button>
                    {cycle.status === CycleStatus.DRAFT && (
                      <>
                        <Button size="small" startIcon={<EditIcon sx={{ fontSize: '13px !important' }} />} onClick={() => { setMutationError(''); setEditingCycle(cycle); }} sx={{ color: '#64748B', fontSize: '0.8rem' }}>{tr('common.edit', lang)}</Button>
                        <Tooltip title={hasActive ? tr('cycles.closeActiveFirst', lang) : ''} placement="top">
                          <span><Button size="small" onClick={() => handleActivate(cycle)} disabled={hasActive} sx={{ color: '#10B981', fontSize: '0.8rem' }}>{tr('common.activate', lang)}</Button></span>
                        </Tooltip>
                        <Button size="small" startIcon={<DeleteIcon sx={{ fontSize: '13px !important' }} />} onClick={() => { if (window.confirm(tr('cycles.confirmDelete', lang))) deleteMut.mutate(cycle.id); }} sx={{ color: '#EF4444', ml: 'auto', fontSize: '0.8rem' }}>{tr('common.delete', lang)}</Button>
                      </>
                    )}
                    {cycle.status === CycleStatus.ACTIVE && (
                      <Button size="small" onClick={() => { if (window.confirm(tr('cycles.confirmClose', lang))) closeMut.mutate(cycle.id); }} sx={{ color: '#D97706', ml: 'auto', fontSize: '0.8rem' }}>{tr('cycles.closeCycle', lang)}</Button>
                    )}
                  </Box>
                </Box>
              </motion.div>
            );
          })}
        </Box>
      )}

      <CreateCycleWizard open={wizardOpen} onClose={() => setWizardOpen(false)} existingCycles={cycles ?? []} />
      {editingCycle && (
        <EditDialog
          open={!!editingCycle} cycle={editingCycle} onClose={() => setEditingCycle(null)}
          onSubmit={d => updateMut.mutate({ id: editingCycle.id, ...d })}
          isLoading={updateMut.isPending} existingCycles={cycles ?? []} serverError={mutationError}
        />
      )}
    </motion.div>
  );
};

export default Cycles;
